import React, { useState, useEffect } from 'react';
import { storageService, UploadedImage } from '../../../services/storageService';
import Button from '../../atoms/Button';
import Icon from '../../atoms/Icon';
import './HomepageImageUploader.css';

interface HomepageImageUploaderProps {
  sectionId?: string;
  currentImageSrc?: string;
  onImageSelect: (imageUrl: string) => void;
  className?: string;
}


const HomepageImageUploader: React.FC<HomepageImageUploaderProps> = ({
  sectionId,
  currentImageSrc,
  onImageSelect,
  className = ''
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string>(currentImageSrc || '');

  // Get the folder path for this section
  const getFolderPath = () => {
    if (!sectionId) return 'homepage-sections/new-section';
    if (sectionId === 'hero-section') return storageService.getHeroSectionFolderPath();
    return storageService.getHomepageSectionFolderPath(sectionId);
  };

  const folderPath = getFolderPath();

  useEffect(() => {
    loadUploadedImages();
  }, [folderPath]);

  useEffect(() => {
    setSelectedImage(currentImageSrc || '');
  }, [currentImageSrc]);

  const loadUploadedImages = async () => {
    try {
      setLoadingImages(true);
      const images = await storageService.getImagesFromFolder(folderPath);
      setUploadedImages(images);
    } catch (error) {
      console.error('Error loading uploaded images:', error);
      console.warn('Could not load images for section:', folderPath);
    } finally {
      setLoadingImages(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    
    try {
      const fileArray = Array.from(files);
      const newImages = await storageService.uploadImages(fileArray, folderPath);
      
      setUploadedImages(prev => [...prev, ...newImages]);
      
      // Auto-select the first uploaded image if no image is currently selected
      if (!selectedImage && newImages.length > 0) {
        setSelectedImage(newImages[0].url);
        onImageSelect(newImages[0].url);
      }
      
    } catch (error) {
      console.error('Error uploading images:', error);
      alert('Failed to upload images. Please try again.');
    } finally {
      setIsUploading(false);
      // Reset the input
      e.target.value = '';
    }
  };

  const handleImageSelect = (imageUrl: string) => {
    setSelectedImage(imageUrl);
    onImageSelect(imageUrl);
  };

  const handleDeleteImage = async (imagePath: string, imageName: string) => {
    if (!window.confirm(`Are you sure you want to delete "${imageName}"?`)) {
      return;
    }

    try {
      await storageService.deleteImage(imagePath);
      
      // Remove from local state
      setUploadedImages(prev => prev.filter(img => img.fullPath !== imagePath));
      
      // If the deleted image was selected, clear the selection
      if (selectedImage === uploadedImages.find(img => img.fullPath === imagePath)?.url) {
        setSelectedImage('');
        onImageSelect('');
      }
      
    } catch (error) {
      console.error('Error deleting image:', error);
      alert('Failed to delete image. Please try again.');
    }
  };

  return (
    <div className={`homepage-image-uploader ${className}`.trim()}>
      <div className="image-uploader-header">
        <h4>Section Image</h4>
        <div className="upload-controls">
          <input
            type="file"
            id="image-upload"
            accept="image/*"
            multiple
            onChange={handleImageUpload}
            style={{ display: 'none' }}
          />
          <Button
            variant="secondary"
            size="small"
            disabled={isUploading}
            className="upload-button"
            onClick={() => document.getElementById('image-upload')?.click()}
          >
            <Icon type="upload" size="small" />
            {isUploading ? 'Uploading...' : 'Upload Images'}
          </Button>
        </div>
      </div>

      {isUploading && (
        <div className="upload-progress">
          <div className="spinner">⟳</div>
          <span>Uploading images...</span>
        </div>
      )}

      {loadingImages && (
        <div className="loading-images">
          <div className="spinner">⟳</div>
          <span>Loading images...</span>
        </div>
      )}

      {uploadedImages.length > 0 && (
        <div className="image-gallery">
          <h5>Available Images for this Section</h5>
          <div className="image-grid">
            {uploadedImages.map((image) => (
              <div
                key={image.fullPath}
                className={`image-item ${selectedImage === image.url ? 'selected' : ''}`}
                onClick={() => handleImageSelect(image.url)}
              >
                <img src={image.url} alt={image.name} />
                <div className="image-overlay">
                  <div className="image-actions">
                    <button
                      className="action-button select-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleImageSelect(image.url);
                      }}
                      title="Select this image"
                    >
                      ✓
                    </button>
                    <button
                      className="action-button delete-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteImage(image.fullPath, image.name);
                      }}
                      title="Delete this image"
                    >
                      <Icon type="delete" size="small" />
                    </button>
                  </div>
                </div>
                {selectedImage === image.url && (
                  <div className="selected-badge">
                    ✓ Selected
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {uploadedImages.length === 0 && !loadingImages && (
        <div className="no-images">
          <Icon type="image" size="large" />
          <p>No images uploaded for this section yet.</p>
          <p>Upload images to get started.</p>
        </div>
      )}

      {selectedImage && (
        <div className="selected-image-preview">
          <h5>Selected Image</h5>
          <div className="preview-container">
            <img src={selectedImage} alt="Selected" />
            <button
              className="clear-selection"
              onClick={() => {
                setSelectedImage('');
                onImageSelect('');
              }}
              title="Clear selection"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomepageImageUploader;
